import React, { useState } from 'react';
import { X } from 'lucide-react';
import { apiCall } from './invoiceApi';

/**
 * Reminder Modal
 * Modal for recording payment reminders
 */
const ReminderModal = ({ invoice, language, onClose, onSave }) => {
  const [reminderType, setReminderType] = useState('first');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiCall(`/api/invoicing/invoices/${invoice.id}/reminders`, {
        method: 'POST',
        body: JSON.stringify({
          reminder_type: reminderType,
          send_email: false,
          notes,
        }),
      });
      onSave();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const t = language === 'fr' ? {
    title: 'Enregistrer un rappel',
    invoice: 'Facture',
    type: 'Type de rappel',
    types: { first: '1er rappel', second: '2ème rappel', final: 'Mise en demeure' },
    notes: 'Notes',
    previousReminders: 'Rappels précédents',
    save: 'Enregistrer',
    cancel: 'Annuler',
  } : {
    title: 'Herinnering registreren',
    invoice: 'Factuur',
    type: 'Type herinnering',
    types: { first: '1e herinnering', second: '2e herinnering', final: 'Ingebrekestelling' },
    notes: 'Opmerkingen',
    previousReminders: 'Vorige herinneringen',
    save: 'Opslaan',
    cancel: 'Annuleren',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">{t.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-orange-50 rounded-lg p-3">
            <p className="text-sm text-gray-600">{t.invoice}: <span className="font-medium text-gray-900">{invoice.invoice_number}</span></p>
            <p className="text-sm text-orange-600 font-medium">€{(invoice.amount_due || invoice.total_incl || 0).toFixed(2)} {language === 'fr' ? 'dû' : 'te betalen'}</p>
          </div>
          
          {invoice.reminders_sent?.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">{t.previousReminders}:</p>
              {invoice.reminders_sent.map((rem, idx) => (
                <p key={idx} className="text-xs text-gray-500">
                  • {t.types[rem.reminder_type]} - {rem.sent_date}
                </p>
              ))}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.type}</label>
            <select
              value={reminderType}
              onChange={(e) => setReminderType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              {Object.entries(t.types).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.notes}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg">
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium"
            >
              {saving ? '...' : t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReminderModal;
